import { useEffect, useMemo, useState } from 'react';

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

const formatCountdown = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const m = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const s = totalSeconds % SECONDS_PER_MINUTE;
  return `${h}h : ${String(m).padStart(2, '0')}m : ${String(s).padStart(2, '0')}s`;
};

type UseCountdownReturn = {
  timeRemaining: number | null;
  formatted: string | null;
};

export function useCountdown(offerExpiry?: string | null): UseCountdownReturn {
  const expiryTime = useMemo<number | null>(() => {
    return offerExpiry ? new Date(offerExpiry).getTime() : null;
  }, [offerExpiry]);

  const [timeRemaining, setTimeRemaining] = useState<number | null>(() => {
    if (!expiryTime) return null;
    return Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
  });

  const [formatted, setFormatted] = useState<string | null>(() => {
    if (!expiryTime) return null;
    const secs = Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
    return formatCountdown(secs);
  });

  useEffect(() => {
    if (!expiryTime) {
      setTimeRemaining(null);
      setFormatted(null);
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const tick = (): void => {
      const secs = Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));

      setTimeRemaining(secs);
      setFormatted(formatCountdown(secs));

      if (secs > 0) {
        const msToNextSecond = 1000 - (Date.now() % 1000);
        timeoutId = setTimeout(tick, msToNextSecond);
      }
    };

    tick();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [expiryTime]);

  return { timeRemaining, formatted };
}
